// Shared modal shell used by AgendaModal, DescriptionModal, PresentersModal,
// and the various ManageModals (Series, Presenters, Locations).
// Matches the hand-rolled pattern in RegistrationModal (no radix dependency).
// Handles ESC key, overlay click, body scroll lock, and a11y attributes.
//
// Inline mode (Round 4 admin redesign): when `inline` is true, the component
// renders its body as a flat in-flow card instead of an overlay dialog. This
// lets the same ManageModal components be embedded directly into top-level
// admin panels (Series, Presenters, Locations sections of the sidebar) without
// having to fork their inner content. Scroll lock + ESC handlers are skipped
// in inline mode; the X close button is hidden.
import { useEffect } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Tailwind max-width class, e.g. "max-w-2xl". Defaults to max-w-3xl. */
  maxWidth?: string;
  /** When true, render as a flat in-flow card (no overlay, no scroll lock). */
  inline?: boolean;
  children: React.ReactNode;
}

export function Modal({ open, onClose, title, maxWidth = 'max-w-3xl', inline = false, children }: ModalProps) {
  // ESC closes the modal (overlay mode only).
  useEffect(() => {
    if (!open || inline) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, inline]);

  // Lock background scroll while open (overlay mode only).
  useEffect(() => {
    if (!open || inline) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, inline]);

  if (!open) return null;

  if (inline) {
    return (
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-uif-green-dark">{title}</h2>
        </div>
        <div className="px-5 py-5">{children}</div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="fixed inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />

      <div
        className={`relative bg-white rounded-lg shadow-xl w-full ${maxWidth} max-h-[90vh] flex flex-col`}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 flex-shrink-0">
          <h2 id="modal-title" className="text-lg font-semibold text-uif-green-dark">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-5">
          {children}
        </div>
      </div>
    </div>
  );
}
